import { useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import {
  Banknote,
  ChevronLeft,
  CreditCard,
  ShieldCheck,
  FileCheck2,
  Landmark,
  Phone,
  RefreshCcw,
  Receipt,
  FileText,
  Pencil,
  Plus,
  Server,
  Trash2,
  Wallet,
  Eye,
  ExternalLink,
  Link2,
  Link2Off,
  Mail,
  MessageSquare,
  Send,
} from 'lucide-react';
import {
  BILLING_PERIODS,
  invoiceNumber,
  receiptNumber,
  type BillingCard,
  type BillingQuote,
  type BillingStatus,
  type C2bClaim,
  type PaymentRow,
  type ShareLink,
  type Wallet as WalletLedger,
  type WalletEntry,
  type ShareLinkMinted,
  type ShareSendChannel,
  type ShareSendResult,
} from '@simpleacs/shared';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  PageHeader,
  Pill,
  type PillTone,
  Spinner,
  StatCard,
  TBody,
  THead,
  TRow,
  Table,
} from '../components/ui';
import { api } from '../lib/api';
import { currencyExponent, formatMoney, formatDate } from '../lib/format';
import { useSession } from '../lib/session';
import { useTasks } from '../lib/tasks';
import { dateTime, INVOICE_TONE, METHOD_LABEL, monthDate, STATUS_TONE } from './billing/common';
import type { Invoice, Organization, Page } from '@simpleacs/shared';
import type { BillingSettingsView } from './settings/GatewayCredentials';

/**
 * The billing page — and, when a bill is due, the only page. The spec is the
 * operator's: charge per OLT, everyone bills on the 1st, each OLT's first
 * month free, the period chosen here at payment time. The network itself
 * never stops for an unpaid bill; only these pages wall.
 */
/**
 * Amounts come from `@simpleacs/shared` now, not from a local `money()`.
 *
 * The API renders these same invoices into PDFs, and a page that groups or
 * rounds an amount differently from its own printed copy is the one
 * discrepancy an invoice cannot survive. The old local helper also divided by
 * 100 unconditionally, which is wrong for any currency that is not
 * two-decimal.
 */
const money = formatMoney;

/**
 * What the wall says about the card on file — one sentence per thing the
 * renewal tick will actually do, read off `card.renewal` (computed by the API
 * from the tick's own rules), never from the switch alone. "Renews
 * automatically within the hour" used to be promised from `autoRenew &&
 * !expired`, and that was false in exactly the states where the payer had to
 * act: after the month's three refusals, while a renewal sat pending, after
 * the month's one renewal had bought an older arrears month. Null when there
 * is nothing worth saying (switch off, expired) — the wall is already telling
 * them to pay.
 */
function renewalLine(card: NonNullable<BillingStatus['card']>): string | null {
  const which = `•••• ${card.last4 ?? '????'}`;
  switch (card.renewal) {
    case 'scheduled':
      return `A card on file (${which}) renews automatically within the hour; paying here now settles it sooner.`;
    case 'retrying':
      return `The card on file (${which}) was refused; it is tried again after ${
        card.nextAttemptAt ? dateTime(card.nextAttemptAt) : 'a day'
      }. Paying here now settles it sooner.`;
    case 'exhausted':
      return `The card on file (${which}) was refused three times this month, so renewals on it resume next month — this bill needs a payment here.`;
    case 'waiting':
      return `A renewal on the card on file (${which}) is awaiting Paystack's answer; nothing more is charged until it answers.`;
    case 'charged':
      return `The card on file (${which}) already renewed one month this month, and one month is all a renewal buys — what is still owed needs a payment here.`;
    case 'unavailable':
      return `The card on file (${which}) cannot be charged right now — card renewals are switched off on this platform — so this bill needs a payment here.`;
    case 'expired':
      return `The card on file (${which}) has expired, so it cannot renew — this bill needs a payment here, and paying by card with "keep this card" ticked replaces it.`;
    case 'off':
      return `The card on file (${which}) is set not to renew — switch it on below, or pay here.`;
  }
}

export function BillingPage() {
  const { session, activeOrg, can } = useSession();
  const isSuperadmin = session?.user.role === 'superadmin';
  const hasOrg = Boolean(activeOrg);

  const status = useQuery({
    queryKey: ['billing', 'status'],
    queryFn: () => api.get<BillingStatus>('/billing/status'),
    refetchInterval: 30_000,
  });

  return (
    <>
      <PageHeader
        title="Billing"
        description="Per-OLT platform billing — everyone bills on the 1st, each OLT's first month is free."
      />
      {status.isLoading ? (
        <div className="flex justify-center p-16">
          <Spinner className="size-6" />
        </div>
      ) : status.error ? (
        <ErrorNotice error={status.error} />
      ) : status.data ? (
        <div className="space-y-5">
          <StatusCards status={status.data} />
          {status.data.provisional && (
            <div className="rounded-lg border border-warn/30 bg-warn-soft px-4 py-3 text-sm text-warn">
              <span className="font-medium">A bank transfer proof is under review.</span> Access
              stays open while the platform owner checks it — if it is rejected, the wall returns.
            </div>
          )}
          {hasOrg && status.data.blocked && (
            <div className="rounded-lg border border-danger/30 bg-danger-soft px-4 py-3 text-sm text-danger">
              <span className="font-medium">
                Payment due since {status.data.dueSince ? monthDate(status.data.dueSince) : 'this month'}.
              </span>{' '}
              The rest of the platform is walled until the bill is settled — the OLTs, ONUs and
              sweeps keep running the whole time.
              {status.data.card && renewalLine(status.data.card) && (
                <> {renewalLine(status.data.card)}</>
              )}
            </div>
          )}
          {hasOrg && <PayCard status={status.data} />}
          {hasOrg && <CardOnFileCard />}
          {hasOrg && <WalletCard status={status.data} />}
          {hasOrg && <OltTrialCard status={status.data} />}
          <BillingAdminTabs />
          {hasOrg && can('billing.manage') && <HistoryCard />}
          {isSuperadmin && <ReviewCard />}
        </div>
      ) : null}
    </>
  );
}

function StatusCards({ status }: { status: BillingStatus }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <StatCard
        label="Billable OLTs"
        value={status.billableOlts}
        icon={<Server className="size-4" />}
      />
      <StatCard label="Monthly total" value={money(status.monthlyMinor, status.currency)} />
      <StatCard
        label="Due now"
        value={money(status.amountDueMinor, status.currency)}
        tone={status.amountDueMinor > 0 ? 'danger' : 'success'}
      />
      <StatCard
        label="Paid through"
        value={
          status.paidThrough && status.amountDueMinor === 0
            ? monthDate(status.paidThrough)
            : status.paidThrough
              ? 'in arrears'
              : 'nothing billable yet'
        }
      />
      {/* Money already handed over that no bill has spent yet. Shown to every
          member, because being told what your own organization has in hand is
          not a privilege — the LEDGER behind it is, and lives in WalletCard. */}
      <StatCard
        label="Credit in hand"
        value={money(status.walletMinor ?? 0, status.currency)}
        icon={<Wallet className="size-4" />}
      />
    </div>
  );
}

function PayCard({ status }: { status: BillingStatus }) {
  const { can, session } = useSession();
  const queryClient = useQueryClient();
  const [months, setMonths] = useState<number>(1);
  const [phone, setPhone] = useState('');
  const [bankRef, setBankRef] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  /**
   * The wizard's position (operator's call): period → currency → gateway →
   * pay. One decision per screen, and each one narrows the next — the
   * currency list comes from what the gateways can actually charge, and the
   * gateway list from what supports the chosen currency. The old flat form
   * showed every method at once and let a payer pick one that could not take
   * their currency; here that combination cannot be reached.
   */
  const [step, setStep] = useState(0);
  const [method, setMethod] = useState<'mpesa' | 'stripe' | 'paystack' | 'bank' | null>(null);
  /**
   * "Keep this card for renewals" — Paystack only, and off by default: a card
   * on file is something a payer asks for in so many words. It travels with
   * the payment (`keepCard`) so whichever path confirms it — the webhook, the
   * return, the check — can honour it; nothing is stored until then.
   */
  const [keepCard, setKeepCard] = useState(false);

  // Every currency some available method can charge in — preset by the
  // gateways (M-PESA: KES only; Stripe/Paystack: USD and KES) and narrowed
  // to what the platform has priced.
  const currencyChoices = [
    ...new Set(
      Object.values(status.methods)
        .filter((m) => m.available)
        .flatMap((m) => m.currencies),
    ),
  ];
  const [currency, setCurrency] = useState(
    currencyChoices.includes(status.currency) ? status.currency : (currencyChoices[0] ?? status.currency),
  );

  const quote = useQuery({
    queryKey: ['billing', 'quote', months, currency],
    queryFn: () => api.get<BillingQuote>(`/billing/quote?months=${months}&currency=${currency}`),
    retry: false,
  });
  // M-PESA settles in KES whatever the page shows — its own number when the
  // chosen currency differs, so nobody is surprised by the prompt.
  const kesQuote = useQuery({
    queryKey: ['billing', 'quote', months, 'KES'],
    queryFn: () => api.get<BillingQuote>(`/billing/quote?months=${months}&currency=KES`),
    enabled: status.methods.mpesa.available && currency !== 'KES',
    retry: false,
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['billing'] });
  };

  const payMpesa = useMutation({
    mutationFn: () => api.post<{ prompt: string }>('/billing/pay/mpesa', { months, phone }),
    onSuccess: async (r) => {
      setError(null);
      setNotice(r.prompt);
      await refresh();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not start the payment'),
  });
  const payRedirect = useMutation({
    mutationFn: (method: 'stripe' | 'paystack') =>
      api.post<{ url: string }>(`/billing/pay/${method}`, {
        months,
        currency: status.methods[method].currencies.includes(currency)
          ? currency
          : status.methods[method].currencies[0],
        ...(method === 'paystack' && keepCard ? { keepCard: true } : {}),
      }),
    onSuccess: (r) => {
      window.location.href = r.url;
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not start the payment'),
  });

  const uploadProof = async (e: FormEvent) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) {
      setError('Choose the proof file — PDF, PNG or JPG.');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const query = new URLSearchParams({
        months: String(months),
        reference: bankRef.trim(),
        ...(status.methods.bank.currencies.includes(currency) ? { currency } : {}),
      });
      const res = await fetch(`/api/billing/pay/bank?${query.toString()}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
        throw new Error(body?.error?.message ?? `Upload failed (${res.status})`);
      }
      setNotice('Proof submitted — access stays open while it is reviewed.');
      setBankRef('');
      if (fileRef.current) fileRef.current.value = '';
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  if (!can('billing.manage')) {
    return (
      <Card title="Pay">
        <p className="p-5 text-sm text-muted">
          Paying needs the billing capability — ask your administrator
          {session ? '' : ' to sign in'} to settle the bill from this page.
        </p>
      </Card>
    );
  }

  // Gateways that can actually charge the chosen currency — the step-3 list.
  const gatewaysFor = (c: string) =>
    (['mpesa', 'stripe', 'paystack', 'bank'] as const).filter(
      (m) => status.methods[m].available && status.methods[m].currencies.includes(c),
    );
  const gateways = gatewaysFor(currency);

  const STEPS = ['Period', 'Currency', 'How to pay', 'Pay'];
  const goto = (n: number) => {
    setError(null);
    setNotice(null);
    setStep(n);
  };

  const METHOD_LABEL = {
    mpesa: 'M-PESA',
    stripe: 'Stripe',
    paystack: 'Paystack',
    bank: 'Bank transfer',
    // Never offered in the chooser — nobody starts a credit payment. It is
    // here because the payments table below reads the same map, and a bill
    // settled from the prepay balance has to be labelled as something.
    credit: 'Credit',
  } as const;
  const METHOD_NOTE = {
    mpesa: 'A prompt on the phone. Settles in shillings.',
    stripe: 'Card payment on a hosted Stripe page.',
    paystack: 'Mobile money, card or bank on a hosted Paystack page.',
    bank: 'Transfer, then upload the slip for review.',
  } as const;
  const METHOD_ICON = {
    mpesa: <Phone className="size-4 text-success" />,
    stripe: <CreditCard className="size-4 text-info" />,
    paystack: <Wallet className="size-4 text-info" />,
    bank: <Landmark className="size-4 text-warn" />,
  } as const;

  const periodLabel = (p: number) =>
    p === 1 ? 'Monthly' : p === 3 ? 'Quarterly' : p === 6 ? '6 months' : 'Yearly';

  // Next is only offered once the step's choice has actually been made: a
  // period is always selected, a currency must be priced, and a gateway must
  // be one that takes the chosen currency.
  const canAdvance =
    step === 0
      ? true
      : step === 1
        ? currencyChoices.includes(currency)
        : step === 2
          ? method !== null && gateways.includes(method)
          : false;

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Banknote className="size-4 text-muted" />
          Pay
        </span>
      }
    >
      {/* The trail: where you are, and every decision already made — each one
          clickable, so stepping back never means starting over. */}
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-5 py-3 text-xs">
        {STEPS.map((label, i) => {
          const done = i < step;
          const value =
            i === 0 && done
              ? periodLabel(months)
              : i === 1 && done
                ? currency
                : i === 2 && done && method
                  ? METHOD_LABEL[method]
                  : null;
          return (
            <span key={label} className="flex items-center gap-2">
              {i > 0 && <span className="text-faint">›</span>}
              <button
                type="button"
                disabled={i > step}
                onClick={() => goto(i)}
                className={`rounded-md px-2 py-1 transition-colors ${
                  i === step
                    ? 'bg-primary-soft font-medium text-primary'
                    : done
                      ? 'text-muted hover:text-ink'
                      : 'text-faint'
                }`}
              >
                {label}
                {value && <span className="ml-1.5 font-mono text-[11px]">{value}</span>}
              </button>
            </span>
          );
        })}
      </div>

      <div className="space-y-4 p-5">
        {step === 0 && (
          <>
            <p className="text-sm text-muted">
              How long do you want to pay for? The period is chosen now, not fixed in advance.
            </p>
            <div className="grid gap-2 sm:grid-cols-4">
              {BILLING_PERIODS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setMonths(p)}
                  className={`rounded-lg border px-4 py-3 text-sm transition-colors ${
                    months === p
                      ? 'border-primary bg-primary-soft text-primary'
                      : 'border-line text-muted hover:text-ink'
                  }`}
                >
                  {periodLabel(p)}
                </button>
              ))}
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <p className="text-sm text-muted">
              Which currency do you want to be charged in? Only currencies the platform has priced
              are shown.
            </p>
            <div className="grid gap-2 sm:grid-cols-3">
              {currencyChoices.map((c) => {
                const supported = gatewaysFor(c);
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => {
                      setCurrency(c);
                      setMethod(null);
                    }}
                    className={`rounded-lg border px-4 py-3 text-left transition-colors ${
                      currency === c
                        ? 'border-primary bg-primary-soft text-primary'
                        : 'border-line text-muted hover:text-ink'
                    }`}
                  >
                    <span className="block font-mono text-sm">{c}</span>
                    <span className="block text-[11px] text-faint">
                      {supported.length === 0
                        ? 'no gateway takes it'
                        : supported.map((m) => METHOD_LABEL[m]).join(', ')}
                    </span>
                  </button>
                );
              })}
            </div>
            {currencyChoices.length === 0 && (
              <p className="text-sm text-warn">
                No currency is priced yet — the platform owner sets a rate first.
              </p>
            )}
          </>
        )}

        {step === 2 && (
          <>
            <QuoteLine quote={quote.data} error={quote.error} />
            <p className="text-sm text-muted">
              These take {currency}. Anything that cannot is not shown.
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              {gateways.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMethod(m)}
                  className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-left transition-colors ${
                    method === m
                      ? 'border-primary bg-primary-soft'
                      : 'border-line hover:border-primary'
                  }`}
                >
                  {METHOD_ICON[m]}
                  <span>
                    <span className="block text-sm font-medium text-ink">{METHOD_LABEL[m]}</span>
                    <span className="block text-[11px] text-faint">{METHOD_NOTE[m]}</span>
                  </span>
                </button>
              ))}
            </div>
            {gateways.length === 0 && (
              <p className="text-sm text-warn">
                No gateway currently takes {currency} — step back and choose another currency.
              </p>
            )}
          </>
        )}

        {step === 3 && method && (
          <>
            <QuoteLine quote={quote.data} error={quote.error} />

            {method === 'mpesa' && (
              <div className="space-y-2">
                <Field label="Phone to prompt">
                  <Input
                    placeholder="07XX XXX XXX"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </Field>
                {currency !== 'KES' && kesQuote.data && (
                  <p className="text-xs text-faint">
                    The prompt charges {money(kesQuote.data.amountMinor, 'KES')} — M-PESA settles in
                    shillings.
                  </p>
                )}
                <Button
                  variant="primary"
                  loading={payMpesa.isPending}
                  disabled={!quote.data || phone.trim() === ''}
                  onClick={() => payMpesa.mutate()}
                >
                  Prompt
                </Button>
              </div>
            )}

            {(method === 'stripe' || method === 'paystack') && (
              <div className="space-y-2">
                <p className="text-sm text-muted">
                  You will be taken to {METHOD_LABEL[method]} to pay, and returned here when it is
                  done.
                </p>
                {method === 'paystack' && (
                  <label className="flex cursor-pointer items-start gap-2 text-sm text-ink">
                    <input
                      type="checkbox"
                      className="mt-0.5 size-4 accent-primary"
                      checked={keepCard}
                      onChange={(e) => setKeepCard(e.target.checked)}
                    />
                    <span>
                      Keep this card for renewals
                      <span className="mt-1 block text-xs text-faint">
                        The card stays with Paystack; SimpleACS keeps only a token and the last four
                        digits, and charges each month's bill to it on the 1st.
                      </span>
                    </span>
                  </label>
                )}
                <Button
                  variant="primary"
                  disabled={!quote.data}
                  loading={payRedirect.isPending}
                  onClick={() => payRedirect.mutate(method)}
                >
                  Continue to {METHOD_LABEL[method]}
                </Button>
              </div>
            )}

            {method === 'bank' && (
              <form onSubmit={uploadProof} className="space-y-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Bank reference">
                    <Input
                      value={bankRef}
                      onChange={(e) => setBankRef(e.target.value)}
                      placeholder="Transaction / slip number"
                      required
                      minLength={2}
                    />
                  </Field>
                  <Field label="Proof (PDF, PNG or JPG)">
                    <input
                      ref={fileRef}
                      type="file"
                      accept="application/pdf,image/png,image/jpeg"
                      className="input pt-1.5 text-xs"
                      required
                    />
                  </Field>
                </div>
                <Button type="submit" variant="primary" loading={uploading}>
                  Submit
                </Button>
                <p className="text-[11px] text-faint">
                  The platform owner reviews the proof. Access stays open while it is pending; a
                  rejection brings the wall back.
                </p>
              </form>
            )}
          </>
        )}

        {notice && <p className="text-sm text-success">{notice}</p>}
        {error && <p className="text-sm font-medium text-danger">{error}</p>}
      </div>

      <footer className="flex items-center justify-between gap-3 border-t border-line px-5 py-3">
        {/* Bordered, not ghost: a step-back control that only shows itself on
            hover is one an operator mid-payment cannot find. The variant is
            token-based, so it carries its own contrast in light and dark
            rather than being tuned for one of them. */}
        <span>
          {step > 0 && (
            <Button
              size="sm"
              variant="secondary"
              icon={<ChevronLeft className="size-3.5" />}
              onClick={() => goto(step - 1)}
            >
              Back
            </Button>
          )}
        </span>
        <span className="text-xs text-faint">
          {periodLabel(months)} · {currency}
          {method ? ` · ${METHOD_LABEL[method]}` : ''}
        </span>
        {/* The last step's button IS the payment — Prompt, Continue or Submit
            — so Next stops here rather than duplicating it. */}
        {step < 3 ? (
          <Button
            size="sm"
            variant="primary"
            disabled={!canAdvance}
            onClick={() => goto(step + 1)}
          >
            Next
          </Button>
        ) : (
          <span />
        )}
      </footer>
    </Card>
  );
}

/** The amount and the months it buys, said once and shared by two steps. */
function QuoteLine({ quote, error }: { quote: BillingQuote | undefined; error: unknown }) {
  if (error) return <p className="text-sm text-warn">{(error as Error).message}</p>;
  if (!quote) return <p className="text-sm text-muted">Working out the total…</p>;
  return (
    <p className="rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-muted">
      <b className="text-ink">{money(quote.amountMinor, quote.currency)}</b> covers{' '}
      {monthDate(quote.periodStart)} – {monthDate(quote.periodEnd - 1)}
    </p>
  );
}

function OltTrialCard({ status }: { status: BillingStatus }) {
  const { session } = useSession();
  const isSuperadmin = session?.user.role === 'superadmin';
  const queryClient = useQueryClient();
  const now = Date.now();

  /** Superadmin only: a free OLT never joins any bill — demo, spare, lab. */
  const setExempt = useMutation({
    mutationFn: (input: { id: string; exempt: boolean }) =>
      api.post(`/billing/olts/${encodeURIComponent(input.id)}/exempt`, { exempt: input.exempt }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['billing'] }),
  });

  return (
    <Card title="OLTs on this bill">
      {status.olts.length === 0 ? (
        <EmptyState title="No OLTs yet" description="Billing starts when the first OLT's free month ends." />
      ) : (
        <Table>
          <THead>
            <th className="th">OLT</th>
            <th className="th">Added</th>
            <th className="th">Billing</th>
            {isSuperadmin && <th className="th w-28" />}
          </THead>
          <TBody>
            {status.olts.map((o) => (
              <TRow key={o.id}>
                <td className="td font-medium text-ink">{o.name}</td>
                <td className="td text-muted">{monthDate(o.createdAt)}</td>
                <td className="td">
                  {o.exempt ? (
                    <Pill tone="neutral">free — not billed</Pill>
                  ) : o.billableFrom > now ? (
                    <Pill tone="info">free until {monthDate(o.billableFrom)}</Pill>
                  ) : (
                    <span className="text-muted">since {monthDate(o.billableFrom)}</span>
                  )}
                </td>
                {isSuperadmin && (
                  <td className="td">
                    <Button
                      size="sm"
                      variant="ghost"
                      loading={setExempt.isPending && setExempt.variables?.id === o.id}
                      title={
                        o.exempt
                          ? 'Put this OLT back on the bill'
                          : 'Mark this OLT free — it never joins any bill'
                      }
                      onClick={() => setExempt.mutate({ id: o.id, exempt: !o.exempt })}
                    >
                      {o.exempt ? 'Bill it' : 'Mark free'}
                    </Button>
                  </td>
                )}
              </TRow>
            ))}
          </TBody>
        </Table>
      )}
    </Card>
  );
}

function HistoryCard() {
  const { can, session } = useSession();
  const isSuperadmin = session?.user.role === 'superadmin';
  /** Same rule as the invoices card: publishing a receipt is `billing.manage`. */
  const canShare = can('billing.manage');
  const queryClient = useQueryClient();
  const [checkResult, setCheckResult] = useState<string | null>(null);
  const [minted, setMinted] = useState<{ number: string; url: string } | null>(null);
  const [sending, setSending] = useState<PaymentRow | null>(null);

  const payments = useQuery({
    queryKey: ['billing', 'payments'],
    queryFn: () => api.get<{ items: PaymentRow[] }>('/billing/payments'),
  });

  /**
   * A receipt is a payment, and it needs the same door a bill has: a page the
   * person who paid can be sent, with no account, showing what was paid and
   * whether it landed — and the PDF to keep.
   *
   * The same hook as the invoices list, so the two behave identically: View
   * mints additively and opens the public address, Share hands over the URL
   * and retires the older links, Revoke stops all of it.
   */
  const shares = useShares('payment', canShare);
  const { linkFor } = shares;

  const view = useMutation({
    mutationFn: (p: PaymentRow) =>
      openPublicTab(() => shares.mint(p.id, 'add'), shares.remembered[p.id]).then((r) => ({
        ...r,
        id: p.id,
      })),
    onSuccess: async (r) => {
      setCheckResult(null);
      shares.remember(r.id, r.url);
      if (!r.opened) setMinted({ number: 'this receipt', url: r.url });
      await shares.refresh();
    },
    onError: (e) =>
      setCheckResult(e instanceof Error ? e.message : 'Could not open the public receipt'),
  });

  const revokeShare = useMutation({
    mutationFn: (id: string) => api.delete(`/billing/payments/${encodeURIComponent(id)}/share`),
    onSuccess: async (_r, id) => {
      setCheckResult(null);
      shares.forget(id);
      await shares.refresh();
    },
    onError: (e) => setCheckResult(e instanceof Error ? e.message : 'Could not revoke the link'),
  });

  /**
   * The reconciliation the operator asked for: a pending gateway payment can
   * be checked against the gateway's own record, for when the callback was
   * lost to a network drop mid-payment.
   */
  const check = useMutation({
    mutationFn: (id: string) =>
      api.post<{ state: string; detail: string }>(`/billing/payments/${encodeURIComponent(id)}/check`),
    onSuccess: async (r) => {
      setCheckResult(`Gateway says: ${r.state} — ${r.detail}`);
      await queryClient.invalidateQueries({ queryKey: ['billing'] });
    },
    onError: (e) =>
      setCheckResult(e instanceof Error ? e.message : 'Could not reach the gateway'),
  });

  const items = payments.data?.items ?? [];
  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Receipt className="size-4 text-muted" />
          Payments
        </span>
      }
    >
      {checkResult && <p className="px-5 pt-3 text-sm text-muted">{checkResult}</p>}
      {items.length === 0 ? (
        <EmptyState title="No payments yet" description="Every payment and its verdict lands here." />
      ) : (
        /*
          The same spine as the invoices card above, column for column:
          Number, Amount, Covers, Started, Status, Link, actions. The operator
          asked for it — "in the payments card, replace when with number like
          invoices card, try make them similar, invoices looks prettier" — and
          the reason the invoices card looks better is that it LEADS WITH THE
          THING YOU IDENTIFY A ROW BY. This one led with
          `new Date(createdAt).toLocaleString()`, which is the one fact about a
          payment nobody quotes and the one that pushes every column right.

          Nothing was dropped to make room. Method sits under the amount it
          qualifies and Months under the period it is the length of — both read
          correctly when the other half is missing, which is every payment
          whose period is not known yet.
        */
        <Table>
          <THead>
            <th className="th">Number</th>
            <th className="th">Amount</th>
            <th className="th">Covers</th>
            <th className="th">Started</th>
            <th className="th">Status</th>
            {canShare && <th className="th">Link</th>}
            {can('billing.manage') && <th className="th w-56" />}
          </THead>
          <TBody>
            {items.map((p) => (
              <TRow key={p.id}>
                <td className="td font-mono text-xs">
                  {/* The number is the way in, exactly as on the invoices
                      card: what an operator quotes down a phone is what they
                      click, and it is also what is in the address it opens. */}
                  <Link
                    to={`/billing/payments/${p.number}`}
                    className="text-primary hover:underline"
                  >
                    {receiptNumber(p.number)}
                  </Link>
                </td>
                <td className="td font-medium text-ink">
                  {money(p.amountMinor, p.currency)}
                  <div className="text-[11px] font-normal text-faint">{METHOD_LABEL[p.method]}</div>
                </td>
                <td className="td text-muted">
                  {p.periodStart && p.periodEnd
                    ? `${monthDate(p.periodStart)} – ${monthDate(p.periodEnd - 1)}`
                    : '—'}
                  <div className="text-[11px] text-faint">
                    {p.months} month{p.months === 1 ? '' : 's'}
                  </div>
                </td>
                <td className="td text-muted">{dateTime(p.createdAt)}</td>
                <td className="td">
                  <Pill state tone={STATUS_TONE[p.status]}>{p.status}</Pill>
                  {p.note && (
                    <span className="ml-2 text-[11px] text-faint" title={p.note}>
                      {p.note.length > 40 ? `${p.note.slice(0, 40)}…` : p.note}
                    </span>
                  )}
                </td>
                {/* §8.6: an orphan cell repeating "Covers" sat here, pushing the
                    share/actions cells one column right of their headers. */}
                {canShare && (
                  <td className="td">
                    <ShareState link={linkFor.get(p.id)} />
                  </td>
                )}
                {can('billing.manage') && (
                  // The flex lived on the <td>, which takes the cell out of the
                  // table's column layout — it belongs on an inner wrapper (§8.6).
                  <td className="td"><div className="flex flex-wrap items-center gap-1">
                    {canShare && (
                      <Button
                        size="sm"
                        variant="secondary"
                        icon={<ExternalLink className="size-3.5" />}
                        loading={view.isPending && view.variables?.id === p.id}
                        title="Open the receipt at its public address — the page a payer can be sent"
                        onClick={() => view.mutate(p)}
                      >
                        View
                      </Button>
                    )}
                    {canShare && (
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={<Send className="size-3.5" />}
                        title="Email or text the receipt to whoever paid — they need no account"
                        onClick={() => setSending(p)}
                      >
                        Send
                      </Button>
                    )}
                    {canShare && linkFor.has(p.id) && (
                      /* Labelled for the same reason as the invoices card:
                         revoking is the only thing that ends a link now. */
                      <Button
                        size="sm"
                        variant="ghost"
                        title="Every link to this receipt stops answering at once"
                        icon={<Link2Off className="size-3.5" />}
                        onClick={() => {
                          const n = linkFor.get(p.id)?.links ?? 1;
                          if (
                            confirm(
                              `Stop sharing this receipt? ${n === 1 ? 'Its link' : `All ${n} of its links`} will stop working immediately.`,
                            )
                          )
                            revokeShare.mutate(p.id);
                        }}
                      >
                        Stop sharing
                      </Button>
                    )}
                    {/* Neither a bank transfer nor a credit settlement has a
                        gateway holding a record of it, so neither offers the
                        button — one is confirmed by a reviewer, the other out
                        of money already in hand. */}
                    {p.status === 'pending' && p.method !== 'bank' && p.method !== 'credit' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        icon={<RefreshCcw className="size-3.5" />}
                        loading={check.isPending && check.variables === p.id}
                        title="Ask the gateway what became of this payment — for when a callback was lost"
                        onClick={() => check.mutate(p.id)}
                      >
                        Check with the gateway
                      </Button>
                    )}
                    {p.hasProof && (
                      <a
                        className="text-xs text-primary hover:underline"
                        href={`/api/billing/payments/${encodeURIComponent(p.id)}/proof`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View proof
                      </a>
                    )}
                  </div></td>
                )}
              </TRow>
            ))}
          </TBody>
        </Table>
      )}
      {canShare && items.length > 0 && (
        <p className="border-t border-line px-5 py-2.5 text-[11px] text-faint">
          View opens the receipt at a public address anyone can read without an account — what was
          paid, by what means, whether it landed, and a PDF to keep. It is the same page a card
          payer lands on coming back from the gateway. Send emails or texts that address to
          somebody you name. The link does not expire: it is live until you stop sharing, which
          takes every link to this receipt at once.
        </p>
      )}
      {minted && <ShareLinkDialog minted={minted} onClose={() => setMinted(null)} />}
      {sending && (
        <SendLinkDialog
          what="this receipt"
          path={`/billing/payments/${encodeURIComponent(sending.id)}`}
          onClose={() => setSending(null)}
          invalidate={[shares.queryKey]}
        />
      )}
    </Card>
  );
}

function ReviewCard() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const queue = useQuery({
    queryKey: ['billing', 'review'],
    queryFn: () => api.get<{ items: PaymentRow[] }>('/billing/review'),
    refetchInterval: 60_000,
  });
  const act = useMutation({
    mutationFn: (input: { id: string; approve: boolean }) =>
      api.post(`/billing/review/${encodeURIComponent(input.id)}`, { approve: input.approve }),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['billing'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not record the verdict'),
  });

  const items = queue.data?.items ?? [];
  if (items.length === 0) return null;
  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <FileCheck2 className="size-4 text-muted" />
          Bank proofs awaiting review
          <Pill tone="warn">{items.length}</Pill>
        </span>
      }
    >
      {error && <p className="px-5 pt-3 text-sm text-danger">{error}</p>}
      <div className="divide-y divide-line">
        {items.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
            <span className="text-sm text-muted">
              <Link
                to={`/billing/payments/${encodeURIComponent(p.id)}`}
                className="font-semibold text-primary hover:underline"
              >
                {money(p.amountMinor, p.currency)}
              </Link>{' '}
              · {p.months} month
              {p.months === 1 ? '' : 's'} · ref {p.reference ?? '—'} ·{' '}
              {dateTime(p.createdAt)}
            </span>
            <span className="flex items-center gap-2">
              <a
                className="text-xs text-primary hover:underline"
                href={`/api/billing/payments/${encodeURIComponent(p.id)}/proof`}
                target="_blank"
                rel="noreferrer"
              >
                View proof
              </a>
              <Button
                size="sm"
                variant="success"
                loading={act.isPending && act.variables?.id === p.id && act.variables.approve}
                onClick={() => act.mutate({ id: p.id, approve: true })}
              >
                Approve
              </Button>
              <Button
                size="sm"
                variant="danger"
                loading={act.isPending && act.variables?.id === p.id && !act.variables.approve}
                onClick={() => act.mutate({ id: p.id, approve: false })}
              >
                Reject
              </Button>
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

/**
 * Settings and Invoices as tabs rather than two stacked cards (operator's
 * call), with Settings leading — it is what a platform owner sets once,
 * before there is anything to invoice.
 *
 * An organization only ever has the Invoices half, so it gets that card
 * directly: a tab strip with one tab is a control that decides nothing.
 */
function BillingAdminTabs() {
  const { session } = useSession();
  const isSuperadmin = session?.user.role === 'superadmin';
  const [tab, setTab] = useState<'settings' | 'invoices' | 'paybill'>('settings');

  if (!isSuperadmin) return <InvoicesCard />;

  const TABS = [
    { id: 'settings' as const, label: 'Settings' },
    { id: 'invoices' as const, label: 'Invoices' },
    { id: 'paybill' as const, label: 'Paybill' },
  ];

  return (
    <div>
      <div className="mb-4 flex gap-1 overflow-x-auto border-b border-line">
        {TABS.map((entry) => (
          <button
            key={entry.id}
            type="button"
            onClick={() => setTab(entry.id)}
            className={
              tab === entry.id
                ? 'border-b-2 border-primary px-4 py-2.5 text-sm font-medium text-primary'
                : 'border-b-2 border-transparent px-4 py-2.5 text-sm text-muted hover:text-ink'
            }
          >
            {entry.label}
          </button>
        ))}
      </div>
      {tab === 'settings' ? <SettingsCard /> : tab === 'invoices' ? <InvoicesCard /> : <ClaimsCard />}
    </div>
  );
}

function SettingsCard() {
  const queryClient = useQueryClient();
  const settings = useQuery({
    queryKey: ['billing', 'settings'],
    queryFn: () => api.get<BillingSettingsView>('/billing/settings'),
  });
  const [form, setForm] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {};
      if (form.rate !== undefined && form.rate !== '') {
        body.ratePerOltMinor = Math.round(
          Number(form.rate) * 10 ** currencyExponent(form.currency ?? s?.currency ?? 'KES'),
        );
      }
      if (form.currency) body.currency = form.currency;
      if (form.rateUsd !== undefined && form.rateUsd !== '') {
        body.rates = { USD: Math.round(Number(form.rateUsd) * 10 ** currencyExponent('USD')) };
      }
      if (form.fxUsd !== undefined && form.fxUsd !== '') {
        body.fx = { USD: Number(form.fxUsd) };
      }
      return api.put('/billing/settings', body);
    },
    onSuccess: async () => {
      setError(null);
      setNotice('Saved.');
      setForm({});
      setTimeout(() => setNotice(null), 4000);
      await queryClient.invalidateQueries({ queryKey: ['billing'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not save'),
  });

  const s = settings.data;
  if (!s) return null;
  const set = (k: string) => (e: { target: { value: string } }) =>
    setForm({ ...form, [k]: e.target.value });

  const gatewaysConfigured = [s.mpesa.configured, s.stripe.configured, s.paystack.configured].filter(
    Boolean,
  ).length;

  return (
    <Card title="Rates and currencies">
      <div className="grid gap-4 p-5 sm:grid-cols-2 lg:grid-cols-3">
        <Field label={`Rate per OLT per month (${s.currency}, the default currency)`}>
          <Input
            type="number"
            min={0}
            step="0.01"
            placeholder={(s.ratePerOltMinor / 10 ** currencyExponent(s.currency)).toFixed(
              currencyExponent(s.currency),
            )}
            value={form.rate ?? ''}
            onChange={set('rate')}
          />
        </Field>
        <Field label="Default currency">
          <select className="input" value={form.currency ?? s.currency} onChange={set('currency')}>
            {(s.currencies ?? ['KES', 'USD']).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </Field>
        <div />
        <Field label="Rate per OLT in USD (optional — wins over conversion)">
          <Input
            type="number"
            min={0}
            step="0.01"
            placeholder={
              s.rates?.USD
                ? (s.rates.USD / 10 ** currencyExponent('USD')).toFixed(currencyExponent('USD'))
                : 'not set'
            }
            value={form.rateUsd ?? ''}
            onChange={set('rateUsd')}
          />
        </Field>
        <Field label={`USD per 1 ${s.currency} (fx, used when no USD rate is set)`}>
          <Input
            type="number"
            min={0}
            step="0.0001"
            placeholder={s.fx?.USD ? String(s.fx.USD) : 'not set — USD not offered'}
            value={form.fxUsd ?? ''}
            onChange={set('fxUsd')}
          />
        </Field>
        <div className="flex items-end pb-1 text-sm text-muted">
          <span>
            <Pill state tone={gatewaysConfigured > 0 ? 'success' : 'neutral'}>
              {gatewaysConfigured} of 3 gateways configured
            </Pill>{' '}
            — credentials live on the{' '}
            <a className="text-primary hover:underline" href="/settings/api-keys">
              API keys page
            </a>
            .
          </span>
        </div>
      </div>
      {error && <p className="px-5 pb-2 text-sm text-danger">{error}</p>}
      {notice && <p className="px-5 pb-2 text-sm text-success">{notice}</p>}
      <footer className="flex justify-end border-t border-line px-5 py-3.5">
        <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
          Save settings
        </Button>
      </footer>
    </Card>
  );
}

/**
 * The card on file for renewals — what the payer sees of a Paystack token.
 *
 * Brand, last four, expiry, the switch, the last charge and the last refusal
 * in the bank's own words. Behind `billing.manage`, matching `GET /billing/card`
 * — a member sees only the "renews automatically on •••• 4081" line the
 * status carries. Removal confirms INLINE, on the card itself: a modal for a
 * one-line question is the wrong weight, and the rule on this platform is
 * inline cards over new dialogs.
 */
function CardOnFileCard() {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const allowed = can('billing.manage');

  const card = useQuery({
    queryKey: ['billing', 'card'],
    queryFn: () => api.get<{ card: BillingCard | null }>('/billing/card'),
    enabled: allowed,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['billing'] });
  const setAutoRenew = useMutation({
    mutationFn: (autoRenew: boolean) =>
      api.patch<{ card: BillingCard }>('/billing/card', { autoRenew }),
    onSuccess: async () => {
      setError(null);
      await refresh();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not change the switch'),
  });
  const remove = useMutation({
    mutationFn: () => api.delete<{ ok: true }>('/billing/card'),
    onSuccess: async () => {
      setError(null);
      setConfirmRemove(false);
      await refresh();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not remove the card'),
  });

  if (!allowed) return null;
  const c = card.data?.card ?? null;
  // Nothing on file and nothing to say about it: the wizard's checkbox is
  // where a card gets here, so an empty card would only repeat that.
  if (!c) return null;

  const expiry =
    c.expMonth !== null && c.expYear !== null
      ? `${String(c.expMonth).padStart(2, '0')}/${String(c.expYear).slice(-2)}`
      : null;
  /*
    The pill reads `renewal`, what the tick will do this month, rather than
    the switch: a card that is switched on but exhausted, or pending Paystack's
    answer, or unchargeable because the operator switched Paystack off, is
    not a card that "renews automatically" — and the payer who reads that
    while walled would wait for a charge that is not coming.
  */
  const pills: Record<BillingCard['renewal'], { tone: PillTone; label: string }> = {
    scheduled: { tone: 'success', label: 'renews automatically' },
    retrying: { tone: 'warn', label: 'refused — retrying' },
    exhausted: { tone: 'danger', label: 'refused three times this month' },
    waiting: { tone: 'info', label: 'awaiting Paystack' },
    charged: { tone: 'success', label: 'renewed this month' },
    unavailable: { tone: 'neutral', label: 'renewals unavailable' },
    expired: { tone: 'danger', label: 'expired' },
    off: { tone: 'neutral', label: 'renewals off' },
  };
  const pill = pills[c.renewal];

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <ShieldCheck className="size-4 text-muted" />
          Card on file
          <Pill state tone={pill.tone}>{pill.label}</Pill>
        </span>
      }
      action={
        confirmRemove ? (
          <span className="flex items-center gap-2 text-xs text-muted">
            Remove this card?
            <Button size="sm" variant="danger" loading={remove.isPending} onClick={() => remove.mutate()}>
              Remove
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirmRemove(false)}>
              Keep it
            </Button>
          </span>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            icon={<Trash2 className="size-3.5" />}
            onClick={() => setConfirmRemove(true)}
          >
            Remove
          </Button>
        )
      }
    >
      <div className="space-y-3 p-5 text-sm">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span className="flex items-center gap-2 font-medium text-ink">
            <CreditCard className="size-4 text-info" />
            {c.brand ?? c.cardType ?? 'Card'} •••• {c.last4 ?? '????'}
          </span>
          {expiry && (
            <span className={c.expired ? 'text-danger' : 'text-muted'}>
              {c.expired ? 'Expired' : 'Expires'} {expiry}
            </span>
          )}
          {c.bank && <span className="text-faint">{c.bank}</span>}
          <span className="font-mono text-xs text-faint">{c.currency}</span>
        </div>
        <label className="flex cursor-pointer items-start gap-2 text-sm text-ink">
          <input
            type="checkbox"
            className="mt-0.5 size-4 accent-primary"
            checked={c.autoRenew}
            disabled={setAutoRenew.isPending || c.expired}
            onChange={(e) => setAutoRenew.mutate(e.target.checked)}
          />
          <span>
            Renew automatically on this card
            <span className="mt-1 block text-xs text-faint">
              Each month's bill is charged on the 1st. Off keeps the card and stops the charge;
              a refused card is tried at most three times a month, a day apart, and the wall
              goes up as it would with no card at all.
            </span>
          </span>
        </label>
        <p className="text-xs text-muted">
          {c.lastChargedAt ? `Last charged ${dateTime(c.lastChargedAt)}.` : 'Not charged yet.'}
          {c.lastFailure && (
            <>
              {' '}
              <span className="text-danger">
                Last refusal{c.lastAttemptAt ? ` (${dateTime(c.lastAttemptAt)})` : ''}: {c.lastFailure}
              </span>
            </>
          )}
          {/* The retry budget, only once some of it is spent: "1 of 3" is
              what tells a payer whether waiting is an option this month. */}
          {c.attemptsThisMonth > 0 && (
            <>
              {' '}
              {c.attemptsThisMonth} of 3 attempts used this month
              {c.renewal === 'retrying' && c.nextAttemptAt
                ? `; next after ${dateTime(c.nextAttemptAt)}`
                : c.renewal === 'exhausted'
                  ? '; renewals resume next month'
                  : ''}
              .
            </>
          )}
          {c.renewal === 'unavailable' && (
            <>
              {' '}
              Card renewals are switched off on this platform right now — Paystack is off, or the
              rate is not set — so this card will not be charged until that changes.
            </>
          )}
        </p>
        <p className="text-[11px] text-faint">
          The card itself stays with Paystack. SimpleACS holds a token and these display details,
          and removing the card revokes the token there too.
        </p>
        {error && <p className="text-sm font-medium text-danger">{error}</p>}
      </div>
    </Card>
  );
}

/**
 * The prepay wallet: money this organization has handed over that no bill has
 * spent yet, and every movement behind the number.
 *
 * A LEDGER on screen because it is a ledger underneath — the balance is the
 * sum of these rows and nothing caches it, so the page can always answer the
 * only question a balance ever provokes, which is how it got to be that
 * number.
 *
 * Behind `billing.manage`, matching `GET /billing/wallet`. The plain balance
 * is in the stat cards above for everybody; which bills the credit went on,
 * and the note on each movement, is money detail and belongs to whoever
 * handles the money.
 */
function WalletCard({ status }: { status: BillingStatus }) {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const allowed = can('billing.manage');

  const wallet = useQuery({
    queryKey: ['billing', 'wallet'],
    queryFn: () => api.get<WalletLedger>('/billing/wallet'),
    enabled: allowed,
  });

  const apply = useMutation({
    mutationFn: () => api.post<{ settled: number; spentMinor: number }>('/billing/wallet/apply'),
    onSuccess: async (r) => {
      setError(null);
      setNotice(
        r.settled === 0
          ? 'Nothing to settle — no issued bill is fully covered by this balance.'
          : `Settled ${r.settled} ${r.settled === 1 ? 'bill' : 'bills'} for ${money(r.spentMinor, status.currency)}.`,
      );
      await queryClient.invalidateQueries({ queryKey: ['billing'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not apply the credit'),
  });

  if (!allowed) return null;
  const entries = wallet.data?.entries ?? [];
  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Wallet className="size-4 text-muted" />
          Prepay credit
        </span>
      }
      action={
        <Button
          size="sm"
          variant="primary"
          loading={apply.isPending}
          disabled={(wallet.data?.balanceMinor ?? 0) <= 0}
          onClick={() => apply.mutate()}
        >
          Apply
        </Button>
      }
    >
      <p className="border-b border-line px-5 py-3 text-sm text-muted">
        {money(wallet.data?.balanceMinor ?? 0, wallet.data?.currency ?? status.currency)} in hand.
        Credit settles a bill <b>in full or not at all</b>, oldest first — it does not part-pay one,
        and it does not reduce the figure due until a bill is actually settled.
      </p>
      {error && <p className="px-5 pt-3 text-sm text-danger">{error}</p>}
      {notice && <p className="px-5 pt-3 text-sm text-success">{notice}</p>}
      {entries.length === 0 ? (
        <EmptyState
          icon={<Wallet className="size-5" />}
          title="No credit has moved yet"
          description="Money paid to the paybill with no bill behind it lands here."
        />
      ) : (
        <Table>
          <THead>
            <th className="th">When</th>
            <th className="th">Movement</th>
            <th className="th">Why</th>
            <th className="th text-right">Amount</th>
          </THead>
          <TBody>
            {entries.map((entry) => (
              <TRow key={entry.id}>
                <td className="px-4 py-2.5 text-muted">{dateTime(entry.createdAt)}</td>
                <td className="px-4 py-2.5">
                  <Pill state tone={entry.amountMinor >= 0 ? 'success' : 'neutral'}>
                    {WALLET_KIND[entry.kind]}
                  </Pill>
                </td>
                <td className="px-4 py-2.5 text-muted">{entry.note ?? '—'}</td>
                <td className="px-4 py-2.5 text-right font-medium tabular-nums">
                  {entry.amountMinor >= 0 ? '+' : '−'}
                  {money(Math.abs(entry.amountMinor), entry.currency)}
                </td>
              </TRow>
            ))}
          </TBody>
        </Table>
      )}
    </Card>
  );
}

/** What each movement is, in the words an operator would use for it. */
const WALLET_KIND: Record<WalletEntry['kind'], string> = {
  topup: 'Paid in',
  spend: 'Spent on a bill',
  refund: 'Returned',
  transfer_out: 'Moved out',
  transfer_in: 'Moved in',
};

/**
 * Superadmin: every M-PESA paybill message and what became of it.
 *
 * `held` is a WORK QUEUE — money in front of a person waiting for a decision,
 * which is where an unreadable account number, an unknown one, anything
 * Safaricom has not confirmed and anything Safaricom disowned all land. The
 * other two lists are history, read to answer a question about one payment.
 *
 * Deliberately not scoped to an organization: the claims that most need
 * looking at are precisely the ones that belong to nobody yet.
 */
/**
 * What Safaricom has said, in a word.
 *
 * `held` covers several situations that need different actions — fix the
 * credentials, wait, chase Safaricom, or treat it as a forgery — and a queue
 * that shows only "held" makes an operator open every row to find out which.
 *
 * `Denied` and `No answer` are deliberately far apart in wording and tone,
 * because they used to be the same pill: Safaricom's queue timing out was
 * recorded as Safaricom denying the payment, so a real customer's money was
 * shown to an operator as a forgery. Only `Denied` means Safaricom said no.
 */
const VERIFY_STATE: Record<C2bClaim['verifyState'], { label: string; tone: PillTone }> = {
  confirmed: { label: 'Confirmed', tone: 'success' },
  disowned: { label: 'Denied', tone: 'danger' },
  inconclusive: { label: 'No proof', tone: 'warn' },
  waiting: { label: 'Asked', tone: 'info' },
  unanswered: { label: 'No answer', tone: 'warn' },
  unchecked: { label: 'Not asked', tone: 'warn' },
};

function ClaimsCard() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<'held' | 'credited' | 'rejected'>('held');
  const [acting, setActing] = useState<{ claim: C2bClaim; mode: 'apply' | 'move' } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const claims = useQuery({
    queryKey: ['billing', 'c2b', status],
    queryFn: () =>
      api.get<{
        items: C2bClaim[];
        held: number;
        /**
         * How big the claims table has got. Nothing ever deletes a claim —
         * `trans_id` is what stops a second confirmation for the same payment,
         * so a pruned row hands the receipt to whoever wants to re-post it —
         * which leaves the row count as the one quantity with no bound. This
         * is where a person finds out, rather than a log line about a table
         * nobody was watching. `rows` is counted only up to `alarmAt`.
         */
        storage: { rows: number; alarmAt: number; over: boolean };
      }>(`/billing/c2b/claims?status=${status}`),
    refetchInterval: 30_000,
  });

  const reject = useMutation({
    mutationFn: (input: { id: string; reason: string }) =>
      api.post(`/billing/c2b/claims/${encodeURIComponent(input.id)}/reject`, {
        reason: input.reason,
      }),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['billing'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not refuse it'),
  });

  const items = claims.data?.items ?? [];
  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <Phone className="size-4 text-muted" />
          Paybill payments
          {(claims.data?.held ?? 0) > 0 && <Pill tone="warn">{claims.data?.held} waiting</Pill>}
        </span>
      }
      action={
        <select
          className="input h-8 py-0 text-xs"
          value={status}
          onChange={(e) => setStatus(e.target.value as typeof status)}
        >
          <option value="held">Waiting on a human</option>
          <option value="credited">Credited</option>
          <option value="rejected">Refused</option>
        </select>
      }
    >
      <p className="border-b border-line px-5 py-3 text-sm text-muted">
        Every message is written down, believed or not — nothing in the body is treated as proof.
        A payment becomes money when Safaricom confirms it for the same receipt and the same
        amount, or when you say so here.
      </p>
      {claims.data?.storage.over && (
        <p className="border-b border-line bg-warn-soft px-5 py-3 text-sm text-warn">
          This list is holding more than {claims.data.storage.alarmAt.toLocaleString()} paybill
          messages. Nothing here is ever deleted on purpose — a removed message frees its M-PESA
          receipt for somebody else to claim — so archive the older ones from the database rather
          than pruning them in place.
        </p>
      )}
      {error && <p className="px-5 pt-3 text-sm text-danger">{error}</p>}
      {items.length === 0 ? (
        <EmptyState
          icon={<Phone className="size-5" />}
          title={status === 'held' ? 'Nothing is waiting' : 'Nothing here'}
          description="M-PESA paybill messages arrive at the confirmation URL on the gateway credentials page."
        />
      ) : (
        <Table>
          <THead>
            <th className="th">Received</th>
            <th className="th">Payer</th>
            <th className="th">Typed</th>
            <th className="th">Organization</th>
            <th className="th">Safaricom</th>
            <th className="th">What happened</th>
            <th className="th text-right">Amount</th>
            <th className="th w-40" />
          </THead>
          <TBody>
            {items.map((claim) => (
              <TRow key={claim.id}>
                <td className="px-4 py-2.5 text-muted">{dateTime(claim.receivedAt)}</td>
                <td className="px-4 py-2.5">
                  <div className="font-medium text-ink">{claim.payerName ?? 'Unnamed'}</div>
                  <div className="font-mono text-xs text-faint">
                    {claim.msisdn ?? '—'} · {claim.transId}
                  </div>
                </td>
                <td className="px-4 py-2.5 font-mono text-xs text-muted">{claim.billRef ?? '—'}</td>
                <td className="px-4 py-2.5 text-muted">
                  {claim.orgName ?? '—'}
                  {claim.reassignCount > 0 && (
                    <span className="ml-1 text-xs text-faint">
                      (moved {claim.reassignCount === 1 ? 'once' : `${claim.reassignCount} times`})
                    </span>
                  )}
                </td>
                <td className="px-4 py-2.5">
                  <Pill state tone={VERIFY_STATE[claim.verifyState].tone}>
                    {VERIFY_STATE[claim.verifyState].label}
                  </Pill>
                </td>
                <td className="max-w-sm px-4 py-2.5 text-xs text-muted">{claim.note ?? '—'}</td>
                <td className="px-4 py-2.5 text-right font-medium tabular-nums">
                  {money(claim.amountMinor, claim.currency)}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <div className="flex justify-end gap-1">
                    {claim.status === 'held' && (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setActing({ claim, mode: 'apply' })}
                        >
                          Apply
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            const reason = window.prompt('Why is this being refused?');
                            if (reason) reject.mutate({ id: claim.id, reason });
                          }}
                        >
                          Refuse
                        </Button>
                      </>
                    )}
                    {claim.status === 'credited' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setActing({ claim, mode: 'move' })}
                      >
                        Move
                      </Button>
                    )}
                  </div>
                </td>
              </TRow>
            ))}
          </TBody>
        </Table>
      )}
      {acting && (
        <ClaimDialog claim={acting.claim} mode={acting.mode} onClose={() => setActing(null)} />
      )}
    </Card>
  );
}

/**
 * Apply a held payment to an organization, or move a credited one that landed
 * in the wrong wallet.
 *
 * The organization is NAMED here and never re-derived from what the payer
 * typed — this dialog exists precisely because that reference was unusable or
 * wrong, so re-reading it would be re-making the mistake. Moving asks for a
 * reason, which is kept on the claim and in the platform's own audit trail and
 * is deliberately never written into either organization's wallet.
 */
function ClaimDialog({
  claim,
  mode,
  onClose,
}: {
  claim: C2bClaim;
  mode: 'apply' | 'move';
  onClose: () => void;
}) {
  const tasks = useTasks();
  const [orgId, setOrgId] = useState('');
  const [reason, setReason] = useState('');

  const orgs = useQuery({
    queryKey: ['orgs', 'picker'],
    queryFn: () => api.get<Page<Organization>>('/orgs?limit=200'),
  });

  const choices = (orgs.data?.items ?? []).filter((o) => o.id !== claim.orgId);

  // Confirming closes the dialog at once; the write runs in the tasks tray and
  // every billing card refreshes when it lands. A refusal — "that credit has
  // already settled a bill" — stays in the tray until it is read.
  const submit = () => {
    const org = choices.find((o) => o.id === orgId)?.name ?? 'the organization';
    const amount = money(claim.amountMinor, claim.currency);
    const path = `/billing/c2b/claims/${encodeURIComponent(claim.id)}/${mode === 'apply' ? 'apply' : 'reassign'}`;
    const body =
      mode === 'apply' ? { orgId, ...(reason ? { note: reason } : {}) } : { orgId, reason };
    onClose();
    tasks.start({
      label: `${mode === 'apply' ? 'Apply' : 'Move'} ${amount} (${claim.transId})`,
      run: () => api.post(path, body),
      done: () =>
        mode === 'apply' ? `${amount} applied to ${org}.` : `${amount} moved to ${org}.`,
      invalidate: [['billing']],
    });
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card
        className="w-full max-w-lg"
        title={mode === 'apply' ? 'Apply this payment' : 'Move this payment'}
      >
        <div className="grid gap-4 p-5">
          <p className="text-sm text-muted">
            {money(claim.amountMinor, claim.currency)} from {claim.payerName ?? 'an unnamed payer'},
            M-PESA <span className="font-mono">{claim.transId}</span>
            {claim.billRef ? (
              <>
                , who typed <span className="font-mono">{claim.billRef}</span>
              </>
            ) : (
              ', who typed no account number'
            )}
            .
          </p>
          <Field label="Organization">
            <select className="input" value={orgId} onChange={(e) => setOrgId(e.target.value)}>
              <option value="">Choose an organization…</option>
              {choices.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                  {org.number ? ` (${org.number})` : ''}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label={
              mode === 'apply'
                ? 'How you confirmed it (optional — kept on the payment)'
                : 'Why it is being moved (kept on the payment, never shown to either tenant)'
            }
          >
            <Input value={reason} onChange={(e) => setReason(e.target.value)} />
          </Field>
          {mode === 'move' && (
            <p className="text-xs text-faint">
              Refused if that credit has already settled a bill — void the bill first, which returns
              the credit and takes back the month it bought, then move the payment.
            </p>
          )}
        </div>
        <footer className="flex justify-end gap-2 border-t border-line px-5 py-3.5">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!orgId || (mode === 'move' && reason.trim().length < 3)}
            onClick={submit}
          >
            {mode === 'apply' ? 'Apply' : 'Move'}
          </Button>
        </footer>
      </Card>
    </div>
  );
}

/**
 * Invoices — what the platform ASKED for, beside the payments that record
 * what moved. A superadmin creates, edits, issues, settles and voids them;
 * an organization sees its own, minus drafts, because a draft is not yet a
 * claim on anybody.
 */
function InvoicesCard() {
  const { can, session } = useSession();
  const isSuperadmin = session?.user.role === 'superadmin';
  /**
   * Publishing a bill is `billing.manage`, not superadmin.
   *
   * The operator's correction: the URL option was not showing on the admin
   * side. An org admin cannot raise, edit, issue or void an invoice — all of
   * that below is still `isSuperadmin` — but a bill they have been sent is
   * theirs to forward to whoever actually pays it. The API scopes the same
   * capability by organization, so this is the visible half of one rule rather
   * than a second one.
   */
  const canShare = can('billing.manage');
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Invoice | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [minted, setMinted] = useState<{ number: string; url: string } | null>(null);
  const [sending, setSending] = useState<Invoice | null>(null);

  const q = useQuery({
    queryKey: ['billing', 'invoices'],
    queryFn: () => api.get<{ items: Invoice[] }>('/billing/invoices'),
  });

  /**
   * The live share links, in one call for the whole page. Separate from the
   * invoice list on purpose — see routes/invoice-share.ts, where the link is
   * its own plugin because publishing a bill is its own decision.
   */
  const shares = useShares('invoice', canShare);
  const { linkFor } = shares;
  const refreshShares = shares.refresh;

  /**
   * Minting shows the URL in a dialog because it is the ONLY time it exists
   * here: the platform stores a hash, so an operator who closes this without
   * copying has to share again rather than look it up.
   */
  const share = useMutation({
    mutationFn: (inv: Invoice) =>
      shares.mint(inv.id, 'replace').then((r) => ({ number: invoiceNumber(inv.number), url: r.url })),
    onSuccess: async (r, inv) => {
      setError(null);
      setMinted(r);
      /**
       * `replace` retired every live link on this invoice, so the URL this
       * session had remembered from an earlier View is now dead. Forgetting it
       * is what the comment here used to claim happened and did not: without
       * this call the next View reused the remembered address and opened a
       * 404, which reads as "the platform broke my bill" at exactly the moment
       * the operator is checking what the payer will see.
       *
       * Forgotten rather than replaced with the URL just minted. This one is
       * meant for the payer's inbox, and opening it here would count as their
       * first view — inflating the one figure the operator uses to decide
       * whether the bill has been seen.
       */
      shares.forget(inv.id);
      await refreshShares();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not create the link'),
  });

  /**
   * View: the bill as the payer sees it, in a new tab, at its public address.
   *
   * It mints, because there is nothing else it could do — only the token's
   * hash is stored — and it mints ADDITIVELY, so opening a bill to look at it
   * never breaks the link already sitting in somebody's inbox. Only an issued
   * or paid invoice gets the button: the public page refuses a draft and a
   * void one at read time, and a button that mints a credential to a refusal
   * is a trap.
   */
  const view = useMutation({
    mutationFn: (inv: Invoice) =>
      openPublicTab(() => shares.mint(inv.id, 'add'), shares.remembered[inv.id]).then((r) => ({
        ...r,
        number: invoiceNumber(inv.number),
        id: inv.id,
      })),
    onSuccess: async (r) => {
      setError(null);
      shares.remember(r.id, r.url);
      // The popup was blocked: hand the operator the address rather than
      // spending a mint on nothing.
      if (!r.opened) setMinted({ number: r.number, url: r.url });
      await refreshShares();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not open the public page'),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => api.delete(`/billing/invoices/${encodeURIComponent(id)}/share`),
    onSuccess: async (_r, id) => {
      setError(null);
      shares.forget(id);
      await refreshShares();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not revoke the link'),
  });

  const setStatus = useMutation({
    mutationFn: (input: { id: string; status: Invoice['status'] }) =>
      api.patch(`/billing/invoices/${encodeURIComponent(input.id)}`, { status: input.status }),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['billing', 'invoices'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not update the invoice'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/billing/invoices/${encodeURIComponent(id)}`),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['billing', 'invoices'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not delete the invoice'),
  });

  const items = q.data?.items ?? [];

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <FileText className="size-4 text-muted" />
          Invoices
        </span>
      }
      action={
        isSuperadmin ? (
          <Button size="sm" variant="primary" icon={<Plus className="size-3.5" />} onClick={() => setEditing('new')}>
            New invoice
          </Button>
        ) : undefined
      }
    >
      {error && <p className="px-5 pt-3 text-sm text-danger">{error}</p>}
      {items.length === 0 ? (
        <EmptyState
          title="No invoices"
          description={
            isSuperadmin
              ? 'Raise one for a period, issue it, then mark it paid when the money arrives.'
              : 'Invoices the platform issues to you will appear here.'
          }
        />
      ) : (
        <Table>
          <THead>
            <th className="th">Number</th>
            {isSuperadmin && <th className="th">Organization</th>}
            <th className="th">Amount</th>
            <th className="th">Covers</th>
            <th className="th">Due</th>
            <th className="th">Status</th>
            {canShare && <th className="th">Link</th>}
            {canShare && <th className="th w-64" />}
          </THead>
          <TBody>
            {items.map((inv) => (
              <TRow key={inv.id}>
                <td className="td font-mono text-xs">
                  {/* The number is the way in. It is what an operator quotes
                      down a phone, so it is also what they click — and now
                      also what the address bar shows when they get there. */}
                  <Link
                    to={`/billing/invoices/${inv.number}`}
                    className="text-primary hover:underline"
                  >
                    {invoiceNumber(inv.number)}
                  </Link>
                </td>
                {isSuperadmin && <td className="td text-muted">{inv.orgName ?? '—'}</td>}
                <td className="td font-medium text-ink">{money(inv.amountMinor, inv.currency)}</td>
                <td className="td text-muted">
                  {inv.periodStart && inv.periodEnd
                    ? `${monthDate(inv.periodStart)} – ${monthDate(inv.periodEnd - 1)}`
                    : '—'}
                </td>
                <td className="td text-muted">{inv.dueAt ? monthDate(inv.dueAt) : '—'}</td>
                <td className="td">
                  <Pill state tone={INVOICE_TONE[inv.status] ?? 'neutral'}>{inv.status}</Pill>
                </td>
                {canShare && (
                  <td className="td">
                    <ShareState link={linkFor.get(inv.id)} />
                  </td>
                )}
                {canShare && (
                  <td className="td">
                    <span className="flex flex-wrap justify-end gap-1">
                      {(inv.status === 'issued' || inv.status === 'paid') && (
                        <Button
                          size="sm"
                          variant="secondary"
                          icon={<ExternalLink className="size-3.5" />}
                          loading={view.isPending && view.variables?.id === inv.id}
                          title="Open the public page in a new tab — the address is the link a payer can use"
                          onClick={() => view.mutate(inv)}
                        >
                          View
                        </Button>
                      )}
                      {(inv.status === 'issued' || inv.status === 'paid') && (
                        <Button
                          size="sm"
                          variant="ghost"
                          icon={<Link2 className="size-3.5" />}
                          loading={share.isPending && share.variables?.id === inv.id}
                          title={
                            linkFor.has(inv.id)
                              ? 'Share again — every existing link stops working'
                              : 'Create a link anyone can open and pay without an account'
                          }
                          onClick={() => {
                            if (
                              !linkFor.has(inv.id) ||
                              confirm(
                                `${invoiceNumber(inv.number)} is already shared. Sharing again replaces it — every link already out there stops working immediately.`,
                              )
                            )
                              share.mutate(inv);
                          }}
                        >
                          {linkFor.has(inv.id) ? 'Re-share' : 'Share'}
                        </Button>
                      )}
                      {(inv.status === 'issued' || inv.status === 'paid') && (
                        <Button
                          size="sm"
                          variant="ghost"
                          icon={<Send className="size-3.5" />}
                          title="Email or text the link to whoever pays this — they need no account"
                          onClick={() => setSending(inv)}
                        >
                          Send
                        </Button>
                      )}
                      {linkFor.has(inv.id) && (
                        /*
                          Labelled, not an icon. Revoking is now the only thing
                          that ends a link — nothing expires — so it cannot be
                          the one control on this row a person has to hover to
                          identify.
                        */
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Every link to this invoice stops answering at once"
                          icon={<Link2Off className="size-3.5" />}
                          onClick={() => {
                            const n = linkFor.get(inv.id)?.links ?? 1;
                            if (
                              confirm(
                                `Stop sharing ${invoiceNumber(inv.number)}? ${n === 1 ? 'Its link' : `All ${n} of its links`} will stop working immediately.`,
                              )
                            )
                              revoke.mutate(inv.id);
                          }}
                        >
                          Stop sharing
                        </Button>
                      )}
                      {isSuperadmin && inv.status === 'draft' && (
                        <Button size="sm" variant="secondary" onClick={() => setStatus.mutate({ id: inv.id, status: 'issued' })}>
                          Issue
                        </Button>
                      )}
                      {isSuperadmin && inv.status === 'issued' && (
                        <Button size="sm" variant="success" onClick={() => setStatus.mutate({ id: inv.id, status: 'paid' })}>
                          Mark paid
                        </Button>
                      )}
                      {isSuperadmin && inv.status !== 'void' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          title="Edit"
                          icon={<Pencil className="size-3.5" />}
                          onClick={() => setEditing(inv)}
                        />
                      )}
                      {!isSuperadmin ? null : inv.status === 'draft' ? (
                        <Button
                          size="sm"
                          variant="danger"
                          title="Delete this draft"
                          icon={<Trash2 className="size-3.5" />}
                          onClick={() => {
                            if (confirm(`Delete draft ${invoiceNumber(inv.number)}?`)) remove.mutate(inv.id);
                          }}
                        />
                      ) : inv.status !== 'void' ? (
                        <Button
                          size="sm"
                          variant="danger"
                          title="Void — kept on record, so the numbering has no holes"
                          onClick={() => {
                            if (confirm(`Void ${invoiceNumber(inv.number)}? It stays on record.`))
                              setStatus.mutate({ id: inv.id, status: 'void' });
                          }}
                        >
                          Void
                        </Button>
                      ) : null}
                    </span>
                  </td>
                )}
              </TRow>
            ))}
          </TBody>
        </Table>
      )}
      {canShare && !q.isLoading && (
        <p className="border-t border-line px-5 py-2.5 text-[11px] text-faint">
          {isSuperadmin
            ? 'An invoice is what was asked for; the Payments card below records what moved. A draft is invisible to the organization and deletable; anything issued is voided instead, so the numbering never has a gap. '
            : ''}
          View opens the invoice at its public address — the bill, the ways to pay it and a PDF to
          keep — so the address bar is the link you can send. Share hands you the URL and retires
          every link already out there, which is how a link sent to the wrong inbox is recovered.
          Send emails or texts it to somebody you name, and adds a link rather than replacing what
          is already out. Links do not expire: they stay live until you stop sharing, or until the
          invoice is voided or pulled back to draft, which kills them all at once. Watch the open
          count — it is what tells you a link is being used by more people than you sent it to.
        </p>
      )}
      {editing && (
        <InvoiceDialog
          invoice={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          invalidate={[['billing', 'invoices'], shares.queryKey]}
        />
      )}
      {minted && <ShareLinkDialog minted={minted} onClose={() => setMinted(null)} />}
      {sending && (
        <SendLinkDialog
          what={`invoice ${sending.number}`}
          path={`/billing/invoices/${encodeURIComponent(sending.id)}`}
          onClose={() => setSending(null)}
          invalidate={[shares.queryKey]}
        />
      )}
    </Card>
  );
}

const shareDate = (ts: number) => formatDate(ts);

/**
 * The operator's half of the public links on one list of documents.
 *
 * One hook for both lists, because "is this published, has it been opened, and
 * how do I take it back" is the same question about an invoice and about a
 * receipt, and two copies of it would drift.
 *
 * `remembered` is why View does not mint on every click. The platform stores
 * only the token's hash, so a URL cannot be looked up — but within one sitting
 * the browser has just been handed one, and reusing it means an operator who
 * checks a bill four times has one link out there rather than four. It is
 * cleared whenever the links are revoked, so a remembered URL can never
 * outlive the link it names.
 */
function useShares(kind: 'invoice' | 'payment', enabled: boolean) {
  const queryClient = useQueryClient();
  const queryKey = ['billing', `${kind}-shares`];
  const [remembered, setRemembered] = useState<Record<string, string>>({});

  const q = useQuery({
    queryKey,
    queryFn: () => api.get<{ items: ShareLink[] }>(`/billing/${kind}s/shares`),
    enabled,
  });
  const linkFor = new Map((q.data?.items ?? []).map((l) => [l.targetId, l] as const));
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey });
  };

  const mint = (id: string, mode: 'replace' | 'add') =>
    api.post<ShareLinkMinted>(`/billing/${kind}s/${encodeURIComponent(id)}/share`, { mode });

  const forget = (id: string) =>
    setRemembered((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });

  return {
    linkFor,
    /** The key the dialogs hand to the tasks tray so the list refreshes after a send. */
    queryKey,
    refresh,
    remembered,
    remember: (id: string, url: string) => setRemembered((prev) => ({ ...prev, [id]: url })),
    forget,
    mint,
  };
}

/**
 * Open one document's public page in a new tab.
 *
 * The tab is opened SYNCHRONOUSLY inside the click, before the mint, because a
 * `window.open` that happens after an await is a popup a browser blocks. Its
 * `opener` is nulled straight away — the same protection `rel="noopener"`
 * gives an anchor, which cannot be used here because the address does not
 * exist until the server has made it.
 *
 * Minting is `add`, never `replace`: the operator is looking, not re-sending,
 * and rotating the token would kill the link the payer is holding. When the
 * popup is blocked the URL is handed back instead, so the click is never
 * silently wasted — the caller shows it in the dialog to copy.
 */
async function openPublicTab(
  mint: () => Promise<{ url: string }>,
  remembered: string | undefined,
): Promise<{ url: string; opened: boolean }> {
  const tab = window.open('', '_blank');
  if (tab) tab.opener = null;
  try {
    const url = remembered ?? (await mint()).url;
    if (tab) {
      tab.location.replace(url);
      return { url, opened: true };
    }
    return { url, opened: false };
  } catch (err) {
    tab?.close();
    throw err;
  }
}

/**
 * What the operator needs at a glance: is this document published, since when,
 * and has anybody actually opened it. "Not opened yet" is the useful half — it
 * is the difference between chasing the recipient and chasing the payment.
 *
 * The link COUNT is shown once there is more than one, because there can be:
 * View mints without replacing, so that looking at a bill never breaks the
 * link a payer is holding, and each Send adds one too. Revoke takes all of
 * them, which is what keeps the count something to read rather than something
 * to manage.
 *
 * The last line used to say when the link expired. Nothing expires now, so it
 * says what replaced that — the link is live until somebody stops it — and the
 * open count above it stops being trivia: with no clock winding a leak down on
 * its own, "opened four times and I sent it to one person" is the whole
 * early-warning system.
 */
function ShareState({ link }: { link: ShareLink | undefined }) {
  if (!link) return <span className="text-faint">—</span>;
  return (
    <span className="flex flex-col gap-0.5 text-xs">
      <span className="flex items-center gap-1.5 text-ink">
        <Link2 className="size-3.5 text-muted" />
        Shared {shareDate(link.createdAt)}
        {link.links > 1 && <span className="text-faint">· {link.links} links</span>}
      </span>
      <span className="flex items-center gap-1.5 text-muted">
        <Eye className="size-3.5" />
        {link.viewCount === 0
          ? 'Not opened yet'
          : `${link.viewCount} open${link.viewCount === 1 ? '' : 's'}, last ${shareDate(link.lastViewedAt ?? link.createdAt)}`}
      </span>
      <span className="text-faint">Live until you stop sharing</span>
    </span>
  );
}

/**
 * Send the link to the person who has to act on it, by email or SMS.
 *
 * The recipient is TYPED, every time, and nothing is pre-filled from the
 * organization's users. That is the point of the feature: the person who pays
 * a bill — a finance office, an accountant, whoever holds the card — is not an
 * account here, and offering a guessed address would be the fastest way to
 * send somebody's invoice to the wrong person.
 *
 * Sending ADDS a link rather than replacing what is out there, so texting a
 * bill to somebody who already has it by email leaves both working, and a
 * re-send after "it never arrived" does not break the copy that did.
 *
 * The URL is never shown here, even after a successful send. Share is the one
 * call that hands an address back; this one hands it to a person.
 *
 * Send closes the dialog at once and runs in the tasks tray; the share list
 * refreshes when it lands, and a failed send stays in the tray until read.
 */
function SendLinkDialog({
  what,
  path,
  onClose,
  invalidate,
}: {
  what: string;
  /** `/billing/invoices/<id>` or `/billing/payments/<id>`. */
  path: string;
  onClose: () => void;
  /** The share-list key(s) to refresh once the send lands. */
  invalidate: QueryKey[];
}) {
  const tasks = useTasks();
  const [channel, setChannel] = useState<ShareSendChannel>('email');
  const [to, setTo] = useState('');

  const send = () => {
    // Snapshotted: the dialog is gone by the time the send settles.
    const recipient = to.trim();
    const via = channel;
    onClose();
    tasks.start<ShareSendResult>({
      label: `Send ${what} to ${recipient}`,
      run: () => api.post<ShareSendResult>(`${path}/share/send`, { channel: via, to: recipient, mode: 'add' }),
      done: () => `Sent ${what} to ${recipient} by ${via === 'email' ? 'email' : 'SMS'}.`,
      invalidate,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-xl border border-line bg-surface shadow-2xl">
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">Send {what}</h2>
          <p className="mt-0.5 text-xs text-muted">
            They get a link that opens without an account. It stays valid until you stop sharing,
            so they can come back to it later.
          </p>
        </header>
        <div className="grid gap-3 p-5">
          <Field label="How">
            <select
              className="input"
              value={channel}
              onChange={(e) => setChannel(e.target.value as ShareSendChannel)}
            >
              <option value="email">Email</option>
              <option value="sms">SMS</option>
            </select>
          </Field>
          <Field label={channel === 'email' ? 'Email address' : 'Phone number'}>
            <Input
              value={to}
              autoFocus
              placeholder={channel === 'email' ? 'finance@example.com' : '+254712345678'}
              onChange={(e) => setTo(e.target.value)}
            />
          </Field>
          <p className="text-xs text-faint">
            {channel === 'email'
              ? 'Whoever actually settles this. They do not need an account, and nothing is filled in from your user list — the payer is rarely one of them.'
              : 'International format, e.g. +254712345678. The message names who it is from, because the sender id will not.'}
          </p>
        </div>
        <footer className="flex justify-end gap-2 border-t border-line px-5 py-3.5">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon={
              channel === 'email' ? (
                <Mail className="size-3.5" />
              ) : (
                <MessageSquare className="size-3.5" />
              )
            }
            disabled={to.trim().length < 3}
            onClick={send}
          >
            Send
          </Button>
        </footer>
      </div>
    </div>
  );
}

/**
 * The one moment the URL exists in the interface.
 *
 * The platform stores only the token's hash, so this cannot be re-opened
 * later — an operator who closes it without copying shares again. Saying so on
 * the dialog is not decoration: without it, the natural assumption is that the
 * link is sitting somewhere to be looked up.
 */
function ShareLinkDialog({
  minted,
  onClose,
}: {
  minted: { number: string; url: string };
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg rounded-xl border border-line bg-surface shadow-2xl">
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">Public link for {minted.number}</h2>
          <p className="mt-0.5 text-xs text-muted">
            Anyone with this address can see this document — and nothing else. Send it the way you
            would send the document itself.
          </p>
        </header>
        <div className="grid gap-3 p-5">
          <Input readOnly value={minted.url} onFocus={(e) => e.currentTarget.select()} />
          <p className="text-xs text-faint">
            Copy it now. Only a hash of the token is stored, so this address cannot be shown again —
            if it is lost, share again and a fresh one is made.
          </p>
        </div>
        <footer className="flex justify-end gap-2 border-t border-line px-5 py-3.5">
          <Button
            variant="secondary"
            onClick={() => {
              void navigator.clipboard?.writeText(minted.url).then(() => setCopied(true));
            }}
          >
            {copied ? 'Copied' : 'Copy link'}
          </Button>
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        </footer>
      </div>
    </div>
  );
}

/**
 * Save closes the dialog at once and the write runs in the tasks tray; the
 * invoice list and its share links refresh when it lands.
 */
function InvoiceDialog({
  invoice,
  onClose,
  invalidate,
}: {
  invoice: Invoice | null;
  onClose: () => void;
  /** What the invoices card reads — refreshed once the write lands. */
  invalidate: QueryKey[];
}) {
  const tasks = useTasks();
  const orgs = useQuery({
    queryKey: ['orgs'],
    queryFn: () => api.get<{ items: Array<{ id: string; name: string }> }>('/orgs'),
    enabled: invoice === null,
  });
  const [form, setForm] = useState({
    orgId: invoice?.orgId ?? '',
    amount: invoice
      ? (invoice.amountMinor / 10 ** currencyExponent(invoice.currency)).toFixed(
          currencyExponent(invoice.currency),
        )
      : '',
    currency: invoice?.currency ?? 'KES',
    dueAt: invoice?.dueAt ? new Date(invoice.dueAt).toISOString().slice(0, 10) : '',
    note: invoice?.note ?? '',
  });

  const orgList = orgs.data?.items ?? [];

  const save = () => {
    // Snapshotted: the dialog is gone by the time the write settles.
    const body: Record<string, unknown> = {
      amountMinor: Math.round(Number(form.amount) * 10 ** currencyExponent(form.currency)),
      currency: form.currency,
      dueAt: form.dueAt ? Date.parse(`${form.dueAt}T00:00:00Z`) : null,
      note: form.note.trim() || null,
    };
    const orgId = form.orgId;
    const org = orgList.find((o) => o.id === orgId)?.name ?? 'the organization';
    const shown = money(body.amountMinor as number, form.currency);
    onClose();
    tasks.start({
      label: invoice ? `Save ${invoiceNumber(invoice.number)}` : `Create draft invoice for ${org}`,
      run: () =>
        invoice
          ? api.patch(`/billing/invoices/${encodeURIComponent(invoice.id)}`, body)
          : api.post('/billing/invoices', { ...body, orgId }),
      done: () =>
        invoice
          ? `${invoiceNumber(invoice.number)} saved.`
          : `Draft invoice for ${shown} created for ${org} — issue it when it is ready.`,
      invalidate,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <form
        className="relative w-full max-w-md rounded-xl border border-line bg-surface shadow-2xl"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">
            {invoice ? `Edit ${invoiceNumber(invoice.number)}` : 'New invoice'}
          </h2>
          <p className="mt-0.5 text-xs text-muted">
            {invoice
              ? 'A paid invoice takes only a note; the amount is history.'
              : 'Created as a draft — the organization sees nothing until it is issued.'}
          </p>
        </header>
        <div className="grid gap-4 p-5">
          {!invoice && (
            <Field label="Organization">
              <select
                className="input"
                value={form.orgId}
                onChange={(e) => setForm({ ...form, orgId: e.target.value })}
                required
              >
                <option value="">Choose…</option>
                {orgList.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Amount">
              <Input
                type="number"
                min={0}
                step="0.01"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                required
              />
            </Field>
            <Field label="Currency">
              <select
                className="input"
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value })}
              >
                <option value="KES">KES</option>
                <option value="USD">USD</option>
              </select>
            </Field>
          </div>
          <Field label="Due date (optional)">
            <Input type="date" value={form.dueAt} onChange={(e) => setForm({ ...form, dueAt: e.target.value })} />
          </Field>
          <Field label="Note (optional)">
            <textarea
              className="input min-h-[64px]"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              maxLength={500}
            />
          </Field>
        </div>
        <footer className="flex justify-end gap-2 border-t border-line px-5 py-3.5">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary">
            {invoice ? 'Save' : 'Create draft'}
          </Button>
        </footer>
      </form>
    </div>
  );
}
